import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// web/src/rendering/components/leaves/BarChart.tsx
import { memo } from "react";
import { BarChart as RBarChart, Bar, Cell, ReferenceLine, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, } from "recharts";
import { ChartFrame } from "./ChartFrame";
import { PALETTE, toRechartsRows, colorForSeries, fmtAxis, } from "./chartCommon";
import { useLocaleStore } from "../../../stores/localeStore";
function BarChartImpl({ title, subtitle, x_label, y_label, series = [], height = 360, stacked, groups, annotations, y_format, }) {
    const locale = useLocaleStore((s) => s.locale);
    // CR-2026-05-07-001 follow-up: when the backend emitted groups[] WITHOUT
    // a pre-built series[], derive series from groups here. The chart_tool
    // (chart.py::bar_chart) does this conversion server-side, but the
    // render_synthesizer auto-emits raw groups — without this fallback the
    // chart renders empty.
    let workingSeries = series.filter((s) => s && Array.isArray(s.data));
    if (workingSeries.length === 0 && Array.isArray(groups) && groups.length > 0) {
        const seenLabels = [];
        for (const g of groups) {
            for (const it of g.items ?? []) {
                if (it.label && !seenLabels.includes(it.label))
                    seenLabels.push(it.label);
            }
        }
        workingSeries = seenLabels.map((label) => ({
            name: label,
            data: groups.map((g) => {
                const matched = (g.items ?? []).find((it) => it.label === label);
                return { x: g.name, y: matched ? Number(matched.value ?? 0) : 0 };
            }),
        }));
    }
    const validSeries = workingSeries;
    const rechartsRows = toRechartsRows(validSeries);
    // Suppress the legend when each series corresponds 1:1 to an x-axis
    // category — that pattern arises from the groups-to-series fallback when
    // each group has exactly one item, so the legend just duplicates the
    // x-axis labels (account names appearing twice).
    const oneToOne = validSeries.length > 1 &&
        validSeries.length === rechartsRows.length &&
        validSeries.every((s) => {
            const nonZero = (s.data ?? []).filter((d) => Number(d.y) !== 0);
            return nonZero.length === 1;
        });
    const showLegend = validSeries.length > 1 && !oneToOne;
    // In the 1:1 case we render a single Bar against `__value` so the tooltip
    // shows just the hovered bar's value. Project each row's single non-zero
    // series value (or the matching-x value) onto `__value`.
    const data = oneToOne
        ? rechartsRows.map((row, idx) => {
            const matchName = validSeries[idx]?.name;
            const raw = matchName !== undefined ? row[matchName] : undefined;
            const value = raw !== undefined && raw !== null && Number(raw) !== 0
                ? Number(raw)
                : Number(Object.entries(row)
                    .find(([k, v]) => k !== "x" && Number(v) !== 0)?.[1] ?? 0);
            return { ...row, __value: value };
        })
        : rechartsRows;
    // CR-2026-05-07-001 PR-H: when groups are present, render stacked bars
    // (one X-axis category per account, segments per holding label). But not
    // when each group has exactly one segment — that's a flat bar chart.
    const stackBars = !!stacked || (Array.isArray(groups) && groups.length > 0 && !oneToOne);
    // Long-label handling: truncate display labels (full text shown in tooltip),
    // pick rotation angle from longest label, size axis height accordingly.
    const longestLabel = data.reduce((acc, row) => Math.max(acc, String(row.x ?? "").length), 0);
    const truncateAt = 16;
    const truncateLabel = (s) => {
        const str = String(s ?? "");
        return str.length > truncateAt ? `${str.slice(0, truncateAt - 1)}…` : str;
    };
    const rotateLabels = longestLabel > 8 || data.length > 6;
    // Steeper angle for very long labels so they don't run into each other.
    const labelAngle = !rotateLabels ? 0 : longestLabel > 14 ? -45 : -28;
    // Approximate horizontal pixel width per character at 11px font; rotated
    // labels need vertical room equal to the projected label length.
    const charPx = 6.2;
    const projectedHeight = rotateLabels
        ? Math.min(110, Math.ceil(Math.min(longestLabel, truncateAt) * charPx * Math.abs(Math.sin((labelAngle * Math.PI) / 180))) + 24)
        : 36;
    const xAxisHeight = projectedHeight;
    const bottomMargin = (x_label ? 22 : 8) + (rotateLabels ? 16 : 0);
    return (_jsx(ChartFrame, { title: title, subtitle: subtitle, children: _jsx(ResponsiveContainer, { width: "100%", height: height, children: _jsxs(RBarChart, { data: data, margin: { top: 8, right: 16, bottom: bottomMargin, left: y_label ? 16 : 4 }, barCategoryGap: "22%", children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: "#334155", vertical: false }), _jsx(XAxis, { dataKey: "x", tick: { fill: "#94a3b8", fontSize: 11 }, tickFormatter: (v) => truncateLabel(v), tickLine: false, axisLine: { stroke: "#334155" }, interval: 0, angle: labelAngle, textAnchor: rotateLabels ? "end" : "middle", height: xAxisHeight, label: x_label
                            ? {
                                value: x_label,
                                position: "insideBottom",
                                offset: rotateLabels ? -8 : -2,
                                fill: "#64748b",
                                fontSize: 11,
                            }
                            : undefined }), _jsx(YAxis, { tick: { fill: "#94a3b8", fontSize: 11 }, tickLine: false, axisLine: { stroke: "#334155" }, tickFormatter: (v) => fmtAxis(v, y_format, locale), label: y_label
                            ? { value: y_label, angle: -90, position: "insideLeft", fill: "#64748b", fontSize: 11 }
                            : undefined }), _jsx(Tooltip, { cursor: { fill: "rgba(148, 163, 184, 0.08)" }, contentStyle: {
                            background: "#0f172a",
                            border: "1px solid #334155",
                            borderRadius: 8,
                            fontSize: 12,
                        }, labelStyle: { color: "#cbd5e1", fontWeight: 500 }, itemStyle: { color: "#e2e8f0" }, 
                        // labelFormatter receives the un-truncated x value (axis ticks
                        // are truncated for layout, but the tooltip should show the
                        // full account name).
                        labelFormatter: (label) => String(label ?? ""), formatter: (value, name) => {
                            const num = fmtAxis(typeof value === "number" ? value : String(value), y_format, locale);
                            // In the collapsed 1:1 case dataKey is "__value" — strip the
                            // internal name from the tooltip row so it reads as a clean
                            // "Real estate equity   3,000,000" instead of leaking the key.
                            if (oneToOne || name === "__value")
                                return [num, ""];
                            return [num, String(name ?? "")];
                        } }), showLegend && _jsx(Legend, { wrapperStyle: { fontSize: 11, paddingTop: 8 } }), (annotations ?? []).map((a, i) => {
                        const stroke = "#94a3b8";
                        const dash = a.style === "solid" ? undefined : a.style === "dotted" ? "2 3" : "6 3";
                        if (a.y !== undefined) {
                            return (_jsx(ReferenceLine, { y: a.y, stroke: stroke, strokeDasharray: dash, label: a.label ? { value: a.label, position: "right", fill: "#94a3b8", fontSize: 10 } : undefined }, `ann-y-${i}`));
                        }
                        return (_jsx(ReferenceLine, { x: a.x, stroke: stroke, strokeDasharray: dash, label: a.label ? { value: a.label, position: "top", fill: "#94a3b8", fontSize: 10 } : undefined }, `ann-x-${i}`));
                    }), oneToOne ? (
                    // Collapse N diagonal series into a single series so the
                    // tooltip shows only the hovered bar's value (not every
                    // category with 0 for the unmatched ones). Per-bar colours
                    // are preserved via <Cell>.
                    _jsx(Bar, { dataKey: "__value", radius: [3, 3, 0, 0], maxBarSize: 56, isAnimationActive: false, children: data.map((_row, idx) => (_jsx(Cell, { fill: validSeries[idx]?.color ?? colorForSeries(validSeries[idx]?.name, PALETTE[idx % PALETTE.length]) }, idx))) })) : (validSeries.map((s, i) => (_jsx(Bar, { dataKey: s.name, fill: s.color ?? colorForSeries(s.name, PALETTE[i % PALETTE.length]), radius: [3, 3, 0, 0], maxBarSize: 56, stackId: stackBars ? "stack" : undefined }, s.name))))] }) }) }));
}
// Memoised — see PieChart for rationale.
export const BarChart = memo(BarChartImpl);
